(() => {
  'use strict';

  const normaliseText = (value) => (value || '').replace(/\s+/g, ' ').trim();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const reviewSignature = (node) => {
    const body = node.querySelector('.jdgm-carousel-item__review-body, .jdgm-rev__body, .jdgm-rev__content');
    const reviewer = node.querySelector('.jdgm-carousel-item__reviewer-name, .jdgm-rev__author');
    return `${normaliseText(reviewer?.textContent)}|${normaliseText(body?.textContent).slice(0, 180)}`;
  };

  const normaliseMediaUrl = (value) => {
    const url = normaliseText(value).replace(/&amp;/g, '&');
    if (!url || url === 'about:blank' || url === '#') return '';
    if (/^data:image\/(?:gif|svg\+xml);/i.test(url)) return '';
    if (url.startsWith('//')) return `https:${url}`;
    return url;
  };

  const firstSrcsetUrl = (value) => {
    const first = normaliseText(value).split(',')[0]?.trim().split(/\s+/)[0] || '';
    return normaliseMediaUrl(first);
  };

  const mediaContainerFor = (image) => image.closest(
    '.jdgm-carousel-item__image, .jdgm-carousel-item__review-image, .jdgm-carousel-item__review-picture, .jdgm-carousel-item__product-image, .jdgm-rev__pic-link, .jdgm-rev__pics, picture, figure'
  );

  const prepareReviewMedia = (card) => {
    let pendingMedia = 0;
    let loadedMedia = 0;

    card.querySelectorAll('[style]').forEach((element) => {
      const inlineStyle = element.getAttribute('style') || '';
      const keepStyle = element.matches('.jdgm-star, [data-score]') || /background-image\s*:/i.test(inlineStyle);
      if (!keepStyle) element.removeAttribute('style');
    });

    card.querySelectorAll('img').forEach((image) => {
      const candidates = [
        image.getAttribute('data-src'),
        image.getAttribute('data-lazy-src'),
        image.getAttribute('data-original'),
        image.getAttribute('data-original-src'),
        firstSrcsetUrl(image.getAttribute('data-srcset')),
        firstSrcsetUrl(image.getAttribute('srcset')),
        image.getAttribute('src'),
      ].map(normaliseMediaUrl).filter(Boolean);

      const uniqueCandidates = [...new Set(candidates)];
      const container = mediaContainerFor(image);

      if (!uniqueCandidates.length) {
        image.remove();
        if (container && !container.querySelector('img, video, iframe, [style*="background-image"]')) {
          container.classList.add('mah-review-media--failed');
        }
        return;
      }

      pendingMedia += 1;
      let candidateIndex = 0;
      let settled = false;

      const markLoaded = () => {
        if (settled) return;
        settled = true;
        pendingMedia = Math.max(0, pendingMedia - 1);
        loadedMedia += 1;
        card.classList.remove('mah-review-feature-card--no-media');
        container?.classList.add('mah-review-media--loaded');
      };

      const markFailed = () => {
        if (settled) return;
        candidateIndex += 1;
        if (candidateIndex < uniqueCandidates.length) {
          image.src = uniqueCandidates[candidateIndex];
          return;
        }

        settled = true;
        pendingMedia = Math.max(0, pendingMedia - 1);
        image.removeAttribute('src');
        image.removeAttribute('srcset');
        image.remove();
        if (container) container.classList.add('mah-review-media--failed');
        if (pendingMedia === 0 && loadedMedia === 0 && !card.querySelector('.mah-review-media__background')) {
          card.classList.add('mah-review-feature-card--no-media');
        }
      };

      image.addEventListener('load', markLoaded, { once: true });
      image.addEventListener('error', markFailed);
      const lazySrcset = normaliseText(image.getAttribute('data-srcset'));
      if (lazySrcset) image.srcset = lazySrcset.replace(/&amp;/g, '&');
      image.loading = 'lazy';
      image.decoding = 'async';
      image.classList.add('mah-review-media__image');
      if (!normaliseText(image.alt)) image.alt = 'Customer review image';
      image.src = uniqueCandidates[candidateIndex];

      if (image.complete) {
        if (image.naturalWidth > 0) markLoaded();
        else markFailed();
      }
    });

    card.querySelectorAll('[style*="background-image"]').forEach((element) => {
      const style = element.getAttribute('style') || '';
      const match = style.match(/background-image\s*:\s*url\((['"]?)(.*?)\1\)/i);
      const source = normaliseMediaUrl(match?.[2]);
      if (!source) {
        element.classList.add('mah-review-media--failed');
        element.removeAttribute('style');
        return;
      }
      element.style.backgroundImage = `url("${source.replace(/"/g, '%22')}")`;
      element.classList.add('mah-review-media__background');
      loadedMedia += 1;
    });

    if (pendingMedia === 0 && loadedMedia === 0) {
      card.classList.add('mah-review-feature-card--no-media');
    }
  };

  const cleanReviewCard = (node, index, total) => {
    const card = node.cloneNode(true);
    card.classList.remove('slick-cloned', 'is-selected', 'active');
    card.classList.add('mah-review-feature-card', 'mah-review-carousel__slide');
    card.removeAttribute('aria-hidden');
    card.removeAttribute('tabindex');
    card.removeAttribute('style');
    card.dataset.reviewIndex = String(index);
    card.setAttribute('role', 'group');
    card.setAttribute('aria-roledescription', 'slide');
    card.setAttribute('aria-label', `Review ${index + 1} of ${total}`);

    prepareReviewMedia(card);

    card.querySelectorAll('[aria-hidden="true"]').forEach((element) => {
      if (!element.classList.contains('jdgm-star')) element.removeAttribute('aria-hidden');
    });

    return card;
  };

  const getVisibleCount = () => {
    if (window.matchMedia('(max-width: 640px)').matches) return 1;
    if (window.matchMedia('(max-width: 980px)').matches) return 2;
    return 3;
  };

  const setupCarousel = (section, selected, publishedTotal) => {
    const stage = section.querySelector('[data-mah-review-stage]');
    const ui = section.querySelector('[data-mah-carousel-ui]');
    const viewport = section.querySelector('[data-mah-review-viewport]');
    const track = section.querySelector('[data-mah-review-track]');
    const dots = section.querySelector('[data-mah-review-dots]');
    const previous = section.querySelector('[data-mah-review-prev]');
    const next = section.querySelector('[data-mah-review-next]');
    const toggle = section.querySelector('[data-mah-review-toggle]');
    const toggleIcon = section.querySelector('[data-mah-review-toggle-icon]');
    const currentNode = section.querySelector('[data-mah-review-current]');
    const totalNode = section.querySelector('[data-mah-review-total]');
    const footer = section.querySelector('[data-mah-review-footer]');
    const caption = section.querySelector('[data-mah-review-caption]');

    if (!stage || !ui || !viewport || !track || !dots) return false;

    const fragment = document.createDocumentFragment();
    selected.forEach((item, index) => fragment.appendChild(cleanReviewCard(item, index, selected.length)));
    track.replaceChildren(fragment);

    const slides = Array.from(track.children);
    if (!slides.length) return false;

    let visibleCount = getVisibleCount();
    let pageStarts = [];
    let activePage = 0;
    let autoplayTimer = null;
    let resizeTimer = null;
    let scrollFrame = null;
    let autoplayPaused = section.dataset.autoplay !== 'true' || reduceMotion.matches;
    const autoplayDelay = Math.max(5000, Number.parseInt(section.dataset.autoplayDelay || '6000', 10) || 6000);

    const computePages = () => {
      visibleCount = Math.max(1, Math.min(getVisibleCount(), slides.length));
      pageStarts = [];
      for (let index = 0; index < slides.length; index += visibleCount) pageStarts.push(index);
      if (!pageStarts.length) pageStarts = [0];
      activePage = Math.max(0, Math.min(activePage, pageStarts.length - 1));
    };

    const updateControls = (pageIndex) => {
      activePage = Math.max(0, Math.min(pageIndex, pageStarts.length - 1));
      if (currentNode) currentNode.textContent = String(activePage + 1);
      if (totalNode) totalNode.textContent = String(pageStarts.length);
      previous?.toggleAttribute('disabled', activePage === 0);
      next?.toggleAttribute('disabled', activePage === pageStarts.length - 1);
      dots.querySelectorAll('[data-mah-review-dot]').forEach((dot, dotIndex) => {
        dot.setAttribute('aria-current', dotIndex === activePage ? 'true' : 'false');
      });
    };

    const goToPage = (pageIndex, behavior = 'smooth') => {
      const bounded = Math.max(0, Math.min(pageIndex, pageStarts.length - 1));
      const target = slides[pageStarts[bounded]];
      if (!target) return;
      viewport.scrollTo({
        left: target.offsetLeft,
        behavior: reduceMotion.matches ? 'auto' : behavior,
      });
      updateControls(bounded);
    };

    const rebuildDots = () => {
      dots.replaceChildren();
      pageStarts.forEach((startIndex, pageIndex) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'mah-review-carousel__dot';
        dot.dataset.mahReviewDot = String(pageIndex);
        dot.setAttribute('aria-label', `Go to review group ${pageIndex + 1}`);
        dot.setAttribute('aria-current', pageIndex === activePage ? 'true' : 'false');
        dot.addEventListener('click', () => {
          setPaused(true);
          goToPage(pageIndex);
        });
        dots.appendChild(dot);
      });
      updateControls(activePage);
    };

    const stopAutoplay = () => {
      window.clearInterval(autoplayTimer);
      autoplayTimer = null;
    };

    const startAutoplay = () => {
      stopAutoplay();
      if (autoplayPaused || pageStarts.length < 2 || document.hidden) return;
      autoplayTimer = window.setInterval(() => {
        const targetPage = activePage >= pageStarts.length - 1 ? 0 : activePage + 1;
        goToPage(targetPage);
      }, autoplayDelay);
    };

    const setPaused = (paused) => {
      autoplayPaused = paused;
      if (toggle) {
        toggle.setAttribute('aria-pressed', paused ? 'true' : 'false');
        toggle.setAttribute('aria-label', paused ? 'Play review rotation' : 'Pause review rotation');
      }
      if (toggleIcon) toggleIcon.textContent = paused ? '▶' : 'Ⅱ';
      if (paused) stopAutoplay(); else startAutoplay();
    };

    previous?.addEventListener('click', () => {
      setPaused(true);
      goToPage(activePage - 1);
    });

    next?.addEventListener('click', () => {
      setPaused(true);
      goToPage(activePage + 1);
    });

    toggle?.addEventListener('click', () => setPaused(!autoplayPaused));

    viewport.addEventListener('scroll', () => {
      window.cancelAnimationFrame(scrollFrame);
      scrollFrame = window.requestAnimationFrame(() => {
        let nearestPage = 0;
        let nearestDistance = Number.POSITIVE_INFINITY;
        pageStarts.forEach((startIndex, pageIndex) => {
          const distance = Math.abs(slides[startIndex].offsetLeft - viewport.scrollLeft);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestPage = pageIndex;
          }
        });
        updateControls(nearestPage);
      });
    }, { passive: true });

    viewport.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setPaused(true);
        goToPage(activePage - 1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        setPaused(true);
        goToPage(activePage + 1);
      }
    });

    section.addEventListener('mouseenter', stopAutoplay);
    section.addEventListener('mouseleave', startAutoplay);
    section.addEventListener('focusin', stopAutoplay);
    section.addEventListener('focusout', (event) => {
      if (!section.contains(event.relatedTarget)) startAutoplay();
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stopAutoplay(); else startAutoplay();
    });

    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        const currentReview = pageStarts[activePage] || 0;
        computePages();
        activePage = Math.min(Math.floor(currentReview / visibleCount), pageStarts.length - 1);
        rebuildDots();
        goToPage(activePage, 'auto');
      }, 150);
    }, { passive: true });

    reduceMotion.addEventListener?.('change', (event) => {
      if (event.matches) setPaused(true);
    });

    computePages();
    rebuildDots();
    ui.hidden = false;
    stage.classList.add('is-ready');
    section.dataset.mahReviewReady = 'true';
    goToPage(0, 'auto');
    setPaused(autoplayPaused);

    if (footer) {
      footer.hidden = false;
      if (caption) {
        caption.textContent = publishedTotal > selected.length
          ? `Featuring ${selected.length} selected reviews from ${publishedTotal} published reviews.`
          : `Featuring ${selected.length} published ${selected.length === 1 ? 'review' : 'reviews'}.`;
      }
    }

    return true;
  };

  const buildShowcase = (section) => {
    if (section.dataset.mahReviewReady === 'true') return true;

    const source = section.querySelector('[data-mah-review-source]');
    if (!source) return false;

    const candidates = Array.from(source.querySelectorAll('.jdgm-carousel-item'));
    if (!candidates.length) return false;

    const maxReviews = Math.max(3, Number.parseInt(section.dataset.maxReviews || '6', 10) || 6);
    const unique = [];
    const signatures = new Set();

    candidates.forEach((item) => {
      if (item.classList.contains('slick-cloned')) return;
      const signature = reviewSignature(item);
      if (!signature || signatures.has(signature)) return;
      signatures.add(signature);
      unique.push(item);
    });

    if (!unique.length) return false;

    const selected = unique.slice(0, maxReviews);
    const publishedTotal = Math.max(unique.length, Number.parseInt(section.dataset.totalReviews || '0', 10) || 0);
    source.hidden = true;
    source.setAttribute('aria-hidden', 'true');

    return setupCarousel(section, selected, publishedTotal);
  };

  const initialise = (section) => {
    if (buildShowcase(section)) return;

    const source = section.querySelector('[data-mah-review-source]');
    const stage = section.querySelector('[data-mah-review-stage]');
    if (!source || !stage) return;

    const observer = new MutationObserver(() => {
      if (buildShowcase(section)) observer.disconnect();
    });
    observer.observe(source, { childList: true, subtree: true });

    window.setTimeout(() => {
      if (section.dataset.mahReviewReady !== 'true') {
        stage.classList.add('is-fallback');
        const fallback = document.createElement('p');
        fallback.className = 'mah-review-carousel__fallback';
        fallback.textContent = 'Customer reviews are available on each product page.';
        stage.appendChild(fallback);
      }
      observer.disconnect();
    }, 8000);
  };

  const initialiseAll = (root = document) => {
    root.querySelectorAll('[data-mah-review-showcase]').forEach(initialise);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseAll(), { once: true });
  } else {
    initialiseAll();
  }

  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
})();
